import React, { useState, useEffect } from 'react';
import { Eye, EyeSlash, CheckCircle, XCircle, Warning } from '@phosphor-icons/react/ssr';
import { api } from './services/apiClient';

const AcceptInvitePage = () => {
  const params   = new URLSearchParams(window.location.search);
  const token    = params.get('token') ?? '';

  const [status, setStatus]     = useState('loading'); // loading | valid | invalid | success
  const [inviteInfo, setInvite] = useState(null);
  const [form, setForm]         = useState({ username: '', password: '', confirm: '' });
  const [showPwd, setShowPwd]   = useState({ password: false, confirm: false });
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);

  // Validar token al montar
  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    api.get(`/api/invitations/validate?token=${encodeURIComponent(token)}`)
      .then(data => { setInvite(data); setStatus('valid'); })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (form.password !== form.confirm) { setError('Las contraseñas no coinciden.'); return; }
    if (!form.username.trim()) { setError('El nombre de usuario es requerido.'); return; }

    setSaving(true);
    try {
      await api.post('/api/invitations/accept', {
        token,
        username: form.username.trim(),
        password: form.password
      });
      setStatus('success');
    } catch (err) {
      setError(err.message || 'Error al crear la cuenta.');
    } finally {
      setSaving(false);
    }
  };

  const Card = ({ children }) => (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg, #f1f5f9)' }}>
      <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 12,
        padding: '40px 36px', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb' }}>
        {children}
      </div>
    </div>
  );

  if (status === 'loading') return (
    <Card><p style={{ textAlign: 'center', color: '#64748b' }}>Verificando invitación…</p></Card>
  );

  if (status === 'invalid') return (
    <Card>
      <div style={{ textAlign: 'center' }}>
        <XCircle size={48} color="#ef4444" style={{ marginBottom: 16 }} />
        <h2 style={{ color: '#1e293b', margin: '0 0 8px' }}>Link inválido o expirado</h2>
        <p style={{ color: '#64748b', margin: '0 0 24px' }}>
          Este link de invitación no es válido o ya fue usado. Solicita una nueva invitación a tu administrador.
        </p>
        <a href="/login" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
          Ir al login →
        </a>
      </div>
    </Card>
  );

  if (status === 'success') return (
    <Card>
      <div style={{ textAlign: 'center' }}>
        <CheckCircle size={48} color="#22c55e" style={{ marginBottom: 16 }} />
        <h2 style={{ color: '#1e293b', margin: '0 0 8px' }}>¡Cuenta creada!</h2>
        <p style={{ color: '#64748b', margin: '0 0 24px' }}>Tu cuenta fue creada exitosamente. Ya puedes iniciar sesión.</p>
        <a href="/login"
          style={{ display: 'inline-block', background: '#2563eb', color: '#fff',
            padding: '10px 24px', borderRadius: 6, textDecoration: 'none', fontWeight: 600 }}>
          Iniciar sesión
        </a>
      </div>
    </Card>
  );

  return (
    <Card>
      <h2 style={{ color: '#1e293b', margin: '0 0 4px', fontSize: 22 }}>Crear tu cuenta</h2>
      <p style={{ color: '#64748b', margin: '0 0 24px', fontSize: 14 }}>
        Fuiste invitado como <strong>{inviteInfo?.email}</strong>. Elige un nombre de usuario y contraseña.
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Nombre de usuario
          </label>
          <input
            type="text"
            value={form.username}
            onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
            placeholder="ej: juan.perez"
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d1d5db',
              borderRadius: 6, fontSize: 14, outline: 'none' }}
            required
          />
        </div>

        {[
          { key: 'password', label: 'Contraseña', placeholder: 'Mínimo 8 caracteres' },
          { key: 'confirm',  label: 'Confirmar contraseña', placeholder: '' }
        ].map(({ key, label, placeholder }) => (
          <div key={key} style={{ position: 'relative' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              {label}
            </label>
            <input
              type={showPwd[key] ? 'text' : 'password'}
              value={form[key]}
              onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
              placeholder={placeholder}
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 40px 10px 12px',
                border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, outline: 'none' }}
              required
            />
            <button type="button"
              onClick={() => setShowPwd(p => ({ ...p, [key]: !p[key] }))}
              style={{ position: 'absolute', right: 12, top: 34, background: 'none', border: 'none',
                cursor: 'pointer', color: '#9ca3af', padding: 0 }}>
              {showPwd[key] ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
        ))}

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dc2626',
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '10px 12px', fontSize: 13 }}>
            <Warning size={16} />
            {error}
          </div>
        )}

        <button type="submit" disabled={saving}
          style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6,
            padding: '12px 0', fontSize: 15, fontWeight: 600, cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Creando cuenta…' : 'Crear cuenta'}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#9ca3af' }}>
        ¿Ya tienes cuenta? <a href="/login" style={{ color: '#2563eb' }}>Inicia sesión</a>
      </p>
    </Card>
  );
};

export default AcceptInvitePage;
